import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  Copy,
  History,
  ImagePlus,
  Loader,
  MessageSquare,
  Plus,
  ArrowUp,
  ChevronDown,
  Sparkles,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  copyText,
  cancelChatStream,
  deleteConversation,
  fetchConversation,
  fetchConversations,
  fetchChatSettings,
  updateChatSettings,
  getStoredChatWidth,
  getStoredConversationId,
  getStoredDraft,
  removeStoredDraft,
  setStoredChatWidth,
  setStoredConversationId,
  setStoredDraft,
  streamChatMessage,
} from '../services/chatService';
import './ChatPanel.css';

const SUGGESTIONS = [
  'Промпт: ночной Сеул в дождь, голограммы на витринах, 35mm',
  'Клип: камера всплывает изо льда к северному сиянию',
  'Fashion-кадр: шёлк и стекло над облаками, editorial',
  'Промпт: чайная церемония на крыше среди неоновых вывесок',
  'Видео: медленный пролёт сквозь затопленный торговый центр',
  'Портрет: кибер-гейша в киотоском переулке, мокрый асфальт',
  'Промпт: пустынный отель на закате, длинные тени, IMAX',
  'Клип: капля чернил в воде превращается в город',
  'Предметка: прозрачные наушники на мокром чёрном мраморе',
  'Промпт: ночной поезд пересекает мост над туманным ущельем',
  'Fashion: латекс и лён на ветреной набережной Рейкьявика',
  'Видео: дрон опускается в кратер вулкана, лава под туманом',
  'Промпт: бумажный журавль над рисовыми террасами на рассвете',
  'Клип: камера едет по тоннелю из живых цветов',
  'Портрет: астронавт без шлема в поле подсолнечников',
  'Промпт: ночная библиотека, книги парят между этажами',
  'Видео: макро — муравей несёт каплю росы как планету',
  'Промпт: винтажный спорткар в соляной пустыне, жёсткий свет',
  'Клип: медленный облёт вокруг падающего небоскрёба из льда',
  'Fashion-кадр: красное пальто на фоне айсберга',
  'Промпт: уличная еда в Бангкоке, пар, неон, 50mm',
  'Видео: камера ныряет с водопада в бирюзовую лагуну',
  'Промпт: стеклянная оранжерея в снежной буре, тёплый свет внутри',
  'Клип: театр теней на стене старого метро',
  'Портрет: мастер за гончарным кругом, золотая пыль в воздухе',
  'Промпт: рынок специй, пар и цветной дым, cinematic',
  'Видео: волна застывает в воздухе, камера проходит сквозь неё',
  'Промпт: кошка на рояле в заброшенном концертном зале',
  'Fashion: зеркальный плащ в пустыне Атакама, полдень',
  'Клип: лифт без стен поднимается сквозь облака на рассвете',
];

function pickSuggestions(count = 3) {
  const pool = [...SUGGESTIONS];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

function ModelPicker({ models, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = models.find((item) => item.id === value) || models[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!models.length) return null;

  return (
    <div className={`chat-model-picker ${open ? 'open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="chat-model-trigger"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title="Модель чата"
      >
        <span>{current?.name || 'Модель'}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="chat-model-menu">
          {models.map((item) => (
            <button
              key={item.id}
              type="button"
              className={item.id === current?.id ? 'active' : ''}
              onClick={() => {
                onChange(item.id);
                setOpen(false);
              }}
            >
              <span>{item.name}</span>
              {item.id === current?.id && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function parseContentBlocks(text) {
  const segments = [];
  let rest = text || '';

  while (rest.length) {
    const start = rest.indexOf('```');
    if (start === -1) {
      segments.push({ type: 'text', content: rest });
      break;
    }
    if (start > 0) {
      segments.push({ type: 'text', content: rest.slice(0, start) });
    }
    rest = rest.slice(start + 3);
    const newline = rest.indexOf('\n');
    let lang = '';
    if (newline !== -1 && !rest.slice(0, newline).includes('```')) {
      lang = rest.slice(0, newline).trim();
      rest = rest.slice(newline + 1);
    }
    const end = rest.indexOf('```');
    if (end === -1) {
      segments.push({ type: 'code', lang, content: rest, incomplete: true });
      break;
    }
    segments.push({ type: 'code', lang, content: rest.slice(0, end).replace(/\n$/, '') });
    rest = rest.slice(end + 3);
  }

  return segments;
}

function renderInline(text, keyPrefix) {
  const nodes = [];
  const regex = /(\*\*(.+?)\*\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let match;
  let idx = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      nodes.push(<strong key={`${keyPrefix}-b-${idx}`}>{match[2]}</strong>);
    } else if (match[3]) {
      nodes.push(<code key={`${keyPrefix}-c-${idx}`}>{match[3]}</code>);
    }
    lastIndex = match.index + match[0].length;
    idx += 1;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function TextBlock({ content }) {
  const lines = (content || '').split('\n');
  return (
    <div className="chat-text-block">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        const heading = /^(#{1,4})\s+(.*)$/.exec(trimmed);
        if (heading) {
          const level = heading[1].length;
          const Tag = `h${level}`;
          return (
            <Tag key={index} className={`chat-md-h chat-md-h${level}`}>
              {renderInline(heading[2], `h${index}`)}
            </Tag>
          );
        }
        if (trimmed === '') {
          return <div key={index} className="chat-md-gap" />;
        }
        return (
          <p key={index} className="chat-md-line">
            {renderInline(line, `l${index}`)}
          </p>
        );
      })}
    </div>
  );
}

function CopyButton({ text, label = 'Копировать', compact = false }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (event) => {
    event.stopPropagation();
    try {
      await copyText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  return (
    <button
      type="button"
      className={`chat-copy-btn ${compact ? 'compact' : ''}`}
      onClick={handleCopy}
      title={label}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {!compact && <span>{copied ? 'Скопировано' : label}</span>}
    </button>
  );
}

function MessageContent({ content, streaming, status }) {
  const blocks = useMemo(() => parseContentBlocks(content), [content]);

  return (
    <div className={`chat-message-body ${streaming ? 'streaming' : ''}`}>
      {blocks.map((block, index) => {
        if (block.type === 'code') {
          return (
            <div key={index} className={`chat-code-block ${block.incomplete ? 'incomplete' : ''}`}>
              <div className="chat-code-header">
                <span>{block.lang || 'Промпт'}</span>
                <CopyButton text={block.content} label="Копировать" />
              </div>
              <pre><code>{block.content}</code></pre>
              <div className="chat-code-footer">
                <CopyButton text={block.content} label="Копировать" />
              </div>
            </div>
          );
        }
        return <TextBlock key={index} content={block.content} />;
      })}
      {streaming && <span className="chat-caret" />}
      {streaming && !content && (
        <span className="chat-thinking">{status || 'Думаю...'}</span>
      )}
    </div>
  );
}

function ChatPanel({ open = false, onOpenChange, onBalanceChange }) {
  const [width, setWidth] = useState(() => getStoredChatWidth());
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [visibleSuggestions, setVisibleSuggestions] = useState(pickSuggestions);
  const [chatModel, setChatModel] = useState('');
  const [chatModels, setChatModels] = useState([]);
  const [attachment, setAttachment] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesWrapRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const requestRef = useRef(0);
  const stopTimerRef = useRef(null);
  const streamingRef = useRef(false);
  const stoppedRef = useRef(false);
  const streamTokenRef = useRef(null);
  const widthRef = useRef(width);
  const inputRef = useRef('');
  const conversationIdRef = useRef(conversationId);
  const hydratedRef = useRef(false);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  const scrollToBottom = useCallback(() => {
    const wrap = messagesWrapRef.current;
    if (wrap) {
      wrap.scrollTop = wrap.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streaming, scrollToBottom, open]);

  useEffect(() => {
    if (open) {
      setVisibleSuggestions(pickSuggestions());
    }
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    fetchChatSettings()
      .then((settings) => {
        if (cancelled) return;
        setChatModel(settings.model || '');
        setChatModels(settings.models || []);
        if (settings.balance != null) onBalanceChange?.(settings.balance);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [onBalanceChange]);

  const loadConversations = useCallback(async () => {
    const list = await fetchConversations();
    setConversations(list);
    return list;
  }, []);

  const refreshBalance = useCallback(() => {
    fetchChatSettings()
      .then((settings) => {
        if (settings.balance != null) onBalanceChange?.(settings.balance);
      })
      .catch(() => {});
  }, [onBalanceChange]);

  const abandonStream = useCallback(() => {
    if (!abortRef.current) return;
    requestRef.current += 1;
    if (stopTimerRef.current) {
      window.clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    cancelChatStream(streamTokenRef.current).catch(() => {});
    abortRef.current.abort();
    abortRef.current = null;
    streamingRef.current = false;
    stoppedRef.current = true;
    setStreaming(false);
    window.setTimeout(refreshBalance, 4000);
  }, [refreshBalance]);

  const openConversation = useCallback(async (id) => {
    abandonStream();
    if (hydratedRef.current) {
      setStoredDraft(conversationIdRef.current, inputRef.current);
    }
    const data = await fetchConversation(id);
    setConversationId(data.id);
    setMessages(data.messages || []);
    setAttachment(null);
    setStoredConversationId(data.id);
    setHistoryOpen(false);
    setInput(getStoredDraft(data.id));
    hydratedRef.current = true;
  }, [abandonStream]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = await loadConversations();
        if (cancelled) return;
        const storedId = getStoredConversationId();
        const exists = list.find((item) => item.id === storedId);
        if (exists) {
          await openConversation(exists.id);
        } else if (getStoredDraft(null)) {
          setInput(getStoredDraft(null));
          setHistoryOpen(true);
          hydratedRef.current = true;
        } else if (list[0]) {
          await openConversation(list[0].id);
        } else {
          setInput('');
          setHistoryOpen(true);
          hydratedRef.current = true;
        }
      } catch (error) {
        console.error(error);
        toast.error('Не удалось загрузить чат');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (abortRef.current) {
        cancelChatStream(streamTokenRef.current).catch(() => {});
        abortRef.current.abort();
      }
    };
  }, [loadConversations, openConversation]);

  const startNewChat = () => {
    abandonStream();
    setStoredDraft(conversationIdRef.current, inputRef.current);
    setConversationId(null);
    setMessages([]);
    setAttachment(null);
    setStoredConversationId(null);
    setHistoryOpen(true);
    setInput(getStoredDraft(null));
    setVisibleSuggestions(pickSuggestions());
    hydratedRef.current = true;
    textareaRef.current?.focus();
  };

  const handleModelChange = async (modelId) => {
    const previous = chatModel;
    setChatModel(modelId);
    try {
      const updated = await updateChatSettings(modelId);
      setChatModel(updated.model);
    } catch (error) {
      setChatModel(previous);
      toast.error(error.message || 'Не удалось сохранить модель');
    }
  };

  const handleDeleteConversation = async (event, id) => {
    event.stopPropagation();
    try {
      await deleteConversation(id);
      removeStoredDraft(id);
      const list = await loadConversations();
      if (conversationId === id) {
        if (list[0]) {
          await openConversation(list[0].id);
        } else {
          startNewChat();
        }
      }
      toast.success('Диалог удалён');
    } catch {
      toast.error('Не удалось удалить диалог');
    }
  };

  const resizeInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  };

  useEffect(() => {
    resizeInput();
  }, [input]);

  useEffect(() => {
    const persistDraft = () => {
      setStoredDraft(conversationIdRef.current, inputRef.current);
    };
    window.addEventListener('beforeunload', persistDraft);
    return () => {
      persistDraft();
      window.removeEventListener('beforeunload', persistDraft);
    };
  }, []);

  const attachImage = (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Нужно изображение JPEG, PNG, WebP или GIF');
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error('Изображение больше 6 МБ');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      if (dataUrl) setAttachment({ dataUrl, name: file.name });
    };
    reader.onerror = () => toast.error('Не удалось прочитать изображение');
    reader.readAsDataURL(file);
  };

  const sendMessage = async (text, { includeImage = true } = {}) => {
    const content = (text || '').trim();
    const image = includeImage ? attachment : null;
    if ((!content && !image) || streamingRef.current) return;

    if (stopTimerRef.current) {
      window.clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    streamingRef.current = true;
    stoppedRef.current = false;
    streamTokenRef.current = null;
    setInput('');
    if (image) setAttachment(null);
    removeStoredDraft(conversationId);
    setHistoryOpen(false);
    const optimisticUser = {
      id: `tmp-user-${Date.now()}`,
      role: 'user',
      content,
      image_url: image?.dataUrl || null,
      created_at: new Date().toISOString(),
    };
    const optimisticAssistant = {
      id: `tmp-assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      streaming: true,
    };
    setMessages((prev) => [...prev, optimisticUser, optimisticAssistant]);
    setStreaming(true);

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const controller = new AbortController();
    abortRef.current = controller;
    const isCurrent = () => requestRef.current === requestId;

    try {
      await streamChatMessage({
        conversationId,
        content,
        image: image?.dataUrl,
        model: chatModel || undefined,
        signal: controller.signal,
        onMeta: (meta) => {
          if (!isCurrent()) return;
          if (meta.stream_id) streamTokenRef.current = meta.stream_id;
          if (meta.conversation_id) {
            setConversationId(meta.conversation_id);
            setStoredConversationId(meta.conversation_id);
          }
          if (meta.user_message) {
            setMessages((prev) => {
              const next = [...prev];
              const userIndex = next.findIndex((m) => m.id === optimisticUser.id);
              if (userIndex !== -1) next[userIndex] = meta.user_message;
              return next;
            });
          }
        },
        onDelta: (chunk) => {
          if (!isCurrent() || stoppedRef.current) return;
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = {
                ...last,
                content: `${last.content || ''}${chunk}`,
                streaming: true,
              };
            }
            return next;
          });
        },
        onStatus: (statusText) => {
          if (!isCurrent() || stoppedRef.current) return;
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...last, status: statusText };
            }
            return next;
          });
        },
        onBalance: (balance) => onBalanceChange?.(balance),
        onDone: (message, _usage, balance) => {
          if (balance != null) onBalanceChange?.(balance);
          if (!isCurrent()) return;
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            const text = (message?.content || '').trim();
            if (!text) {
              if (last && last.role === 'assistant' && !(last.content || '').trim()) {
                next.pop();
              } else if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, streaming: false, status: undefined };
              }
              return next;
            }
            if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...message, streaming: false };
            } else {
              next.push({ ...message, streaming: false });
            }
            return next;
          });
          loadConversations().catch(() => {});
        },
        onError: (errorText, balance) => {
          if (balance != null) onBalanceChange?.(balance);
          if (!isCurrent()) return;
          toast.error(errorText);
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant' && !last.content) {
              next.pop();
            } else if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...last, streaming: false };
            }
            return next;
          });
        },
      });
    } catch (error) {
      if (!isCurrent()) return;
      if (error.name === 'AbortError') {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant' && !(last.content || '').trim()) {
            next.pop();
          } else if (last && last.role === 'assistant') {
            next[next.length - 1] = { ...last, streaming: false };
          }
          return next;
        });
        window.setTimeout(refreshBalance, 4000);
      } else {
        toast.error(error.message || 'Ошибка чата');
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant' && !last.content) {
            next.pop();
          }
          return next;
        });
      }
    } finally {
      if (isCurrent()) {
        if (stopTimerRef.current) {
          window.clearTimeout(stopTimerRef.current);
          stopTimerRef.current = null;
        }
        streamingRef.current = false;
        setStreaming(false);
        setMessages((prev) => prev.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
        abortRef.current = null;
        requestAnimationFrame(() => textareaRef.current?.focus());
      }
    }
  };

  const stopGeneration = async () => {
    if (!streamingRef.current || stoppedRef.current) return;
    stoppedRef.current = true;
    streamingRef.current = false;
    setStreaming(false);
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant' && !(last.content || '').trim()) {
        next.pop();
      } else if (last && last.role === 'assistant') {
        next[next.length - 1] = { ...last, streaming: false, status: undefined };
      }
      return next;
    });
    const controller = abortRef.current;
    const streamId = streamTokenRef.current;
    if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
    stopTimerRef.current = window.setTimeout(() => {
      if (abortRef.current === controller) controller?.abort();
    }, 8000);
    try {
      const result = await cancelChatStream(streamId);
      if (abortRef.current !== controller) return;
      if (!result?.ok) controller?.abort();
    } catch {
      if (abortRef.current === controller) controller?.abort();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage(input);
    }
  };

  const handleResizeStart = (event) => {
    event.preventDefault();
    setResizing(true);
    const startX = event.clientX;
    const startWidth = widthRef.current;
    const maxWidth = Math.min(640, Math.round(window.innerWidth * 0.5));

    const onMove = (moveEvent) => {
      const next = Math.min(maxWidth, Math.max(300, startWidth + (startX - moveEvent.clientX)));
      widthRef.current = next;
      setWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      setStoredChatWidth(widthRef.current);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const closeChat = () => onOpenChange?.(false);
  const openChat = () => onOpenChange?.(true);

  const currentTitle = useMemo(() => {
    if (!conversationId) return 'Новый чат';
    const found = conversations.find((item) => item.id === conversationId);
    return found?.title || 'Чат';
  }, [conversationId, conversations]);

  return (
    <>
      <button
        type="button"
        className={`chat-fab ${open ? '' : 'visible'}`}
        onClick={openChat}
        title="Открыть чат"
        aria-label="Открыть чат"
      >
        <MessageSquare size={22} />
      </button>

      <aside
        className={`chat-panel ${open ? 'is-open' : ''} ${resizing ? 'resizing' : ''}`}
        style={open ? { width } : undefined}
        onWheel={(event) => event.stopPropagation()}
        onTouchMove={(event) => event.stopPropagation()}
      >
        <div className="chat-resize-handle" onMouseDown={handleResizeStart} />

        <header className="chat-header">
          <button
            type="button"
            className={`chat-header-title ${historyOpen ? 'active' : ''}`}
            onClick={() => setHistoryOpen((v) => !v)}
            title="История чатов"
          >
            <Sparkles size={16} />
            <span>{currentTitle}</span>
          </button>
          <div className="chat-header-actions">
            <button type="button" onClick={startNewChat} title="Новый чат">
              <Plus size={16} />
            </button>
            <button
              type="button"
              className={historyOpen ? 'active' : ''}
              onClick={() => setHistoryOpen((v) => !v)}
              title="История"
            >
              <History size={16} />
            </button>
            <button type="button" onClick={closeChat} title="Скрыть чат">
              <X size={16} />
            </button>
          </div>
        </header>

        {historyOpen && (
          <div className="chat-history">
            {conversations.length === 0 ? (
              <div className="chat-history-empty">История пока пустая</div>
            ) : (
              conversations.map((item) => (
                <div
                  key={item.id}
                  className={`chat-history-item ${item.id === conversationId ? 'active' : ''}`}
                  onClick={() => openConversation(item.id)}
                >
                  <div className="chat-history-text">
                    <div className="chat-history-title">{item.title || 'Без названия'}</div>
                    <div className="chat-history-date">
                      {item.updated_at
                        ? new Date(item.updated_at).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="chat-history-delete"
                    onClick={(event) => handleDeleteConversation(event, item.id)}
                    title="Удалить"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        <div className="chat-messages" ref={messagesWrapRef}>
          {loading ? (
            <div className="chat-empty">
              <Loader className="chat-spinner" size={22} />
              <p>Загрузка чата...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="chat-empty">
              <MessageSquare size={28} />
              <h3>Помогу с промптами</h3>
              <p>Опишите сцену или попросите готовый промпт для фото- и видеомоделей.</p>
              <div className="chat-suggestions">
                {visibleSuggestions.map((item) => (
                  <button key={item} type="button" onClick={() => sendMessage(item, { includeImage: false })}>
                    {item}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.filter((message) => (
              message.role !== 'assistant' || message.streaming || (message.content || '').trim()
            )).map((message) => (
              <div key={message.id} className={`chat-message ${message.role}`}>
                {message.role === 'assistant' && !!message.content && (
                  <div className="chat-message-toolbar">
                    <CopyButton text={message.content} label="Копировать ответ" compact />
                  </div>
                )}
                {message.role === 'user' ? (
                  <div className="chat-user-message">
                    {message.image_url && (
                      <img className="chat-message-image" src={message.image_url} alt="" />
                    )}
                    {!!message.content && <div className="chat-message-body">{message.content}</div>}
                  </div>
                ) : (
                  <MessageContent
                    content={message.content}
                    streaming={!!message.streaming}
                    status={message.status}
                  />
                )}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-composer">
          {chatModels.length > 0 && (
            <div className="chat-composer-bar">
              <ModelPicker
                models={chatModels}
                value={chatModel}
                onChange={handleModelChange}
                disabled={streaming}
              />
            </div>
          )}
          {attachment && (
            <div className="chat-attach-preview">
              <img src={attachment.dataUrl} alt="" />
              <button type="button" onClick={() => setAttachment(null)} title="Убрать изображение">
                <X size={14} />
              </button>
            </div>
          )}
          <form className="chat-input-wrap" onSubmit={handleSubmit}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            hidden
            onChange={(event) => {
              attachImage(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <button
            type="button"
            className="chat-attach"
            onClick={() => fileInputRef.current?.click()}
            disabled={streaming}
            title="Прикрепить изображение"
          >
            <ImagePlus size={18} />
          </button>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(event) => {
              const value = event.target.value;
              setInput(value);
              setStoredDraft(conversationId, value);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Напишите, что хотите сделать сегодня"
            rows={1}
            disabled={streaming}
          />
          <button
            type={streaming ? 'button' : 'submit'}
            className={`chat-send ${streaming ? 'is-stop' : ''}`}
            disabled={!streaming && !input.trim() && !attachment}
            title={streaming ? 'Остановить' : 'Отправить'}
            onClick={streaming ? (event) => {
              event.preventDefault();
              stopGeneration();
            } : undefined}
          >
            {streaming ? <Square size={14} fill="currentColor" /> : <ArrowUp size={18} />}
          </button>
        </form>
        </div>
      </aside>
    </>
  );
}

export default ChatPanel;
